/**
 * Clean persona field values polluted by DB/ETL artifacts ({ } "" etc.).
 */

const NOISE_LITERALS = new Set([
  'none identified',
  'null',
  'undefined',
  'n/a',
  'na',
]);

export function isPersonFieldNoise(value) {
  if (value == null) return true;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  const t = String(value).trim();
  if (!t) return true;
  if (/^[\{\}"'\s,]+$/.test(t)) return true;
  if (NOISE_LITERALS.has(t.toLowerCase())) return true;
  return false;
}

/** Strip stray braces, empty quotes, and wrapping punctuation from a display string. */
export function sanitizePersonFieldString(value) {
  if (value == null) return '';
  if (typeof value !== 'string') {
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  }

  let s = value.trim();
  if (!s) return '';

  // Unwrap a single pair of outer braces when not JSON object content (no key:value).
  if (s.startsWith('{') && s.endsWith('}') && !/:\s*["[{]/.test(s)) {
    s = s.slice(1, -1).trim();
  }

  s = s.replace(/""/g, '').replace(/''/g, '');
  s = s.replace(/[{}]/g, '');
  s = s.replace(/^["'\s,]+|["'\s,]+$/g, '');
  s = s.replace(/\s+/g, ' ').trim();

  return s;
}

export function formatPersonEducationEntry(entry) {
  if (entry && typeof entry === 'object') {
    const parts = [
      entry.degree,
      entry.field,
      entry.school,
      entry.institution,
      entry.name,
    ]
      .map(sanitizePersonFieldString)
      .filter((p) => !isPersonFieldNoise(p));
    return parts.join(' · ');
  }
  return sanitizePersonFieldString(String(entry ?? ''));
}

/** Split list strings at separator only when (), [], {} depth is zero. */
export function splitAtBracketDepthZero(text, separator) {
  const src = String(text ?? '');
  const out = [];
  let buf = '';
  let depth = 0;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (ch === '(' || ch === '[' || ch === '{') {
      depth += 1;
      buf += ch;
    } else if (ch === ')' || ch === ']' || ch === '}') {
      depth = Math.max(0, depth - 1);
      buf += ch;
    } else if (ch === separator && depth === 0) {
      const piece = buf.trim();
      if (piece) out.push(piece);
      buf = '';
    } else {
      buf += ch;
    }
  }
  const last = buf.trim();
  if (last) out.push(last);
  return out;
}

/**
 * Multi-value persona fields: newline wins, else `;` at bracket depth 0.
 * Never split on comma — commas belong inside an entry.
 */
export function splitPersonaListString(text) {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return [];
  if (/[\r\n]/.test(trimmed)) {
    return trimmed.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  }
  return splitAtBracketDepthZero(trimmed, ';');
}

export function personFieldToArray(value, { splitEntries = true } = {}) {
  if (value == null || value === '') return [];

  let rawItems = [];
  if (Array.isArray(value)) {
    rawItems = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) rawItems = parsed;
      } catch {
        /* fall through to list split */
      }
    }
    if (rawItems.length === 0) {
      if (!splitEntries) {
        rawItems = [trimmed];
      } else if (splitEntries === 'comma') {
        rawItems = trimmed
          .split(/,(?![^(]*\))/)
          .map((s) => s.trim())
          .filter(Boolean);
      } else {
        rawItems = splitPersonaListString(trimmed);
      }
    }
  } else {
    return [];
  }

  return rawItems
    .map((item) => {
      if (item && typeof item === 'object') {
        const cleaned = {};
        for (const [key, val] of Object.entries(item)) {
          const part = sanitizePersonFieldString(val);
          if (!isPersonFieldNoise(part)) cleaned[key] = part;
        }
        return Object.keys(cleaned).length > 0 ? cleaned : null;
      }
      const cleaned = sanitizePersonFieldString(String(item ?? ''));
      return isPersonFieldNoise(cleaned) ? null : cleaned;
    })
    .filter(Boolean);
}

const PERSON_ARRAY_FIELDS = [
  'initiatives',
  'primary_interests',
  'former_employers',
  'education',
  'memberships',
  'public_speaking',
  'events_attended',
  'awards',
];

const PERSON_STRING_FIELDS = [
  'first_name',
  'last_name',
  'role_title',
  'department',
  'country',
  'client_name',
  'persona_score_rationale',
  'account_name',
  'product_affinity',
  'archetype_name',
  'job_title',
  'switchboard',
];

/** Normalize persona record once after load so Profile + overview lists stay clean. */
export function sanitizePersonaRecord(person) {
  if (!person || typeof person !== 'object') return person;

  const out = { ...person };

  for (const key of PERSON_STRING_FIELDS) {
    if (out[key] != null && typeof out[key] === 'string') {
      const cleaned = sanitizePersonFieldString(out[key]);
      out[key] = isPersonFieldNoise(cleaned) ? '' : cleaned;
    }
  }

  for (const key of PERSON_ARRAY_FIELDS) {
    if (out[key] != null) {
      // initiatives is prose — wrapping without splitting avoids sentence fragments.
      out[key] = personFieldToArray(out[key], { splitEntries: key !== 'initiatives' });
    }
  }

  if (Array.isArray(out.market_tiers)) {
    out.market_tiers = personFieldToArray(out.market_tiers, { splitEntries: 'comma' });
  }

  return out;
}
